import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { XCircle, RefreshCw } from 'lucide-react'

function ErrorFallback({ resetError }: { error: Error; resetError: () => void }) {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20 p-4 py-12">
      <div className="mx-auto max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-red-900 dark:text-red-100">
              Algo deu errado
            </CardTitle>
            <CardDescription>
              Um erro inesperado ocorreu. Nossa equipe já foi notificada.
            </CardDescription>
          </CardHeader>

          <CardContent className="flex flex-col gap-2">
            <Button onClick={resetError} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Tentar Novamente
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                resetError()
                navigate('/')
              }}
            >
              Voltar ao Início
            </Button>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Precisa de ajuda?{' '}
              <a
                href="mailto:suporte@slac.com.br"
                className="font-medium text-primary hover:underline"
              >
                Fale com o suporte
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function SentryErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
